// Formula central de costo de compra / utilidad / precio de venta.
// Compartida por:
// - modules/users/costs/staff/crudProductSuppliersByBarcode.vue
// - inc/productPricing/productPricingEditor.vue
// - modules/users/costs/staff/crudImportEditor.vue (grilla masiva, agrega conversion de unidades)
//
// Las 3 vistas resuelven "costo -> precio de venta" y su inversa
// "precio de venta final -> utilidad" con la misma matematica; lo que cambia
// entre ellas es como se resuelve la utilidad efectiva (ver effectiveUtility)
// y pasos extra propios de cada vista (conversion de unidades, redondeo hacia
// arriba en la importacion masiva), que se mantienen en cada componente.

export function toNumber(value) {
  var number = parseFloat(value)
  return isNaN(number) ? 0 : number
}

export function round(value, digits = 2) {
  var factor = Math.pow(10, digits)
  return Math.round((toNumber(value) + Number.EPSILON) * factor) / factor
}

export function roundMoney(value) {
  return round(value, 2)
}

export function roundUtility(value, digits = 8) {
  return round(value, digits)
}

// Redondeo hacia arriba usado solo por la importacion masiva (precios "redondos").
export function ceilToMultiple(value, multiple) {
  var amount = toNumber(value)
  var m = toNumber(multiple)
  if(m > 0) return Math.ceil(amount / m) * m
  return Math.ceil(amount)
}

// Redondeo al multiplo MAS CERCANO (no siempre hacia arriba como ceilToMultiple
// -- esto es una limpieza general de precios, no la proteccion de margen de
// P69). multiple=1 (o 0) redondea a entero ("sin decimales").
export function roundToNearestMultiple(value, multiple) {
  var amount = toNumber(value)
  var m = toNumber(multiple)
  if(m > 0) return Math.round(amount / m) * m
  return Math.round(amount)
}

// Costo de compra sin redondear: lista + adicional fijo, 3 descuentos y 2
// adicionales porcentuales encadenados, + adicional fijo final.
export function rawPurchaseCost(config = {}) {
  var value = toNumber(config.priceList)
  value += toNumber(config.additional)
  value -= value * toNumber(config.percentageDiscount) / 100
  value -= value * toNumber(config.percentageDiscount1) / 100
  value -= value * toNumber(config.percentageDiscount2) / 100
  value += value * toNumber(config.additionalPercentage) / 100
  value += value * toNumber(config.additionalPercentage1) / 100
  value += toNumber(config.additional1)
  return value
}

export function purchaseCostFromList(config = {}) {
  return roundMoney(rawPurchaseCost(config))
}

// Utilidad "efectiva" de una lista de precios: la unica utilidad propia
// valida de un producto es una utilidad positiva. Cero o negativo se
// considera "sin definir" y usa la utilidad default de la lista.
// IMPORTANTE: el backend (PriceListProducts.php, getAmountAttribute /
// getAmountTotalAttribute) hoy NO implementa esto asi -- usa empty() de PHP
// sobre un float, que solo trata el 0 exacto como "sin definir" y SI respeta
// una utilidad negativa cargada. O sea que, mientras el backend no se
// corrija para que valide "> 0" en vez de empty(), un producto con utilidad
// negativa va a mostrar el default aca en el frontend pero el backend va a
// seguir aplicando esa utilidad negativa al calcular/guardar el precio real.
export function effectiveUtility({ utility, defaultUtility } = {}) {
  var u = toNumber(utility)
  return u > 0 ? u : toNumber(defaultUtility)
}

export function isUsingDefaultUtility({ utility, defaultUtility } = {}) {
  return toNumber(utility) === 0 && toNumber(defaultUtility) > 0
}

// Precio neto (sin IVA) a partir de costo + utilidad + descuento de lista.
export function netPriceFromUtility({ cost, utility, discount }) {
  var discountBase = 1 - toNumber(discount) / 100
  return toNumber(cost) * (1 + toNumber(utility) / 100) * discountBase
}

// Inversa de netPriceFromUtility, sin guards (usada cuando el llamador ya
// valido cost/discountBase por su cuenta, ej. crudImportEditor).
export function rawUtilityFromNet({ cost, netPrice, discount }) {
  var discountBase = 1 - toNumber(discount) / 100
  return (toNumber(netPrice) / discountBase / toNumber(cost) - 1) * 100
}

// Igual que rawUtilityFromNet pero con los guards que ya usaban
// crudProductSuppliersByBarcode y productPricingEditor (devuelve null si el
// resultado no es representable).
export function utilityFromNetPrice({ cost, netPrice, discount, digits = 8 }) {
  var baseCost = toNumber(cost)
  var discountBase = 1 - toNumber(discount) / 100
  var net = toNumber(netPrice)
  if(baseCost <= 0 || net <= 0 || discountBase <= 0) return null
  return roundUtility(rawUtilityFromNet({ cost, netPrice, discount }), digits)
}

export function netFromGross(gross, ivaAliquot) {
  var ivaFactor = 1 + toNumber(ivaAliquot) / 100
  return ivaFactor > 0 ? toNumber(gross) / ivaFactor : 0
}

export function grossFromNet(net, ivaAliquot) {
  return toNumber(net) * (1 + toNumber(ivaAliquot) / 100)
}

// Conveniencia directa (costo+utilidad+descuento+iva -> precio de venta),
// sin conversion de unidades. Usada por crudProductSuppliersByBarcode y
// productPricingEditor.
export function salePriceFromCost({ cost, utility, discount, ivaAliquot }) {
  var priceNoIva = netPriceFromUtility({ cost, utility, discount })
  return {
    salePrice: roundMoney(priceNoIva),
    salePriceIva: roundMoney(grossFromNet(priceNoIva, ivaAliquot)),
  }
}

// Conveniencia inversa (precio final c/IVA -> utilidad), sin conversion de
// unidades. Usada por crudProductSuppliersByBarcode y productPricingEditor.
// Misma inversa que salePriceFromCost.
export function utilityFromSalePrice({ cost, salePrice, discount, ivaAliquot }) {
  var ivaFactor = 1 + toNumber(ivaAliquot) / 100
  if(ivaFactor <= 0) return null
  var net = toNumber(salePrice) / ivaFactor
  return utilityFromNetPrice({ cost, netPrice: net, discount })
}
